import React, { useState, useEffect, useRef, useCallback } from "react";
import "rapidoc";
import "./App.css";

const DOCS_BASE_URL = "https://docs.toothfairyai.com/docs";

const GUIDES = [
  {
    value: "a2a-protocol",
    label: "A2A Protocol Guide",
    url: `${DOCS_BASE_URL}/Administrators/a2a`,
  },
  {
    value: "voice-api",
    label: "Voice API Integration",
    url: `${DOCS_BASE_URL}/agent/voice/api`,
  },
  {
    value: "twilio-sms",
    label: "Twilio SMS Integration",
    url: `${DOCS_BASE_URL}/agent/twilio/sms`,
  },
  {
    value: "agentic-ingestion",
    label: "Agentic Ingestion (Python)",
    url: `${DOCS_BASE_URL}/Guides/agentic-ingestion-python`,
  },
  {
    value: "api-postman",
    label: "API Postman Guide",
    url: `${DOCS_BASE_URL}/Guides/toothfairy-api-postman-guide`,
  },
  {
    value: "ai-integration",
    label: "ToothFairyAI Integration",
    url: `${DOCS_BASE_URL}/Guides/toothfairy-ai-integration`,
  },
];

const SPEC_APP = "/openapi.json";
const SPEC_AGENTS = "/aiopenapi.json";
const SPEC_VOICE = "/voiceapi.json";
const SPEC_A2A = "/a2aapi.json";
const SPEC_FINETUNING = "/finetuningapi.json";
const SPEC_MEDIA = "/mediaapi.json";
const SPEC_LIVE = "/liveapi.json";

// Fallback scan order when the explicit mapping does not resolve an element
const ALL_SPECS = [
  SPEC_APP,
  SPEC_AGENTS,
  SPEC_VOICE,
  SPEC_MEDIA,
  SPEC_A2A,
  SPEC_FINETUNING,
  SPEC_LIVE,
];

// The header always mirrors the open spec — never an umbrella grouping — so the
// label and intro the user sees match exactly what the dropdown says they opened.
const SPECS = [
  {
    value: SPEC_APP,
    label: "App",
    intro:
      "The core platform API — create and configure agents, chats, documents, knowledge hub, prompts, hooks, workspaces and administration. Everything the ToothFairyAI app does, available programmatically.",
  },
  {
    value: SPEC_AGENTS,
    label: "Agents & Inference API",
    intro:
      "Run autonomous agents — SSE-streamed /agent conversations, /planner orchestration, background /dispatch and /spawn-agent — and call models directly via /predictions or OpenAI-compatible chat completions. Pay per use, no lock-in, zero data retention by default.",
  },
  {
    value: SPEC_VOICE,
    label: "Voice",
    intro:
      "Programmable voice and telephony — SIP trunk configuration, call tokens, webhooks and health checks powering real-time speech agents.",
  },
  {
    value: SPEC_LIVE,
    label: "Live (Voice & Text based sessions)",
    intro:
      "Mixed-mode conversations — switch between realtime voice and text within a single chat, mix agents per turn, and read a unified transcript. Realtime voice segments stream over WebRTC with interruption and barge-in support; text turns use the standard agent chat contract.",
  },
  {
    value: SPEC_MEDIA,
    label: "Media",
    intro:
      "Generate media programmatically — text-to-speech with an audible LLM pass or direct synthesis, image generation with five models (text-to-image and image editing), Kling video from prompts or still frames, plus voice cloning and background tasks. Pay per use, tracked per workspace.",
  },

  {
    value: SPEC_FINETUNING,
    label: "Fine-Tuning",
    intro:
      "Train and serve your own models — dataset generation, SFT, DPO, GRPO, KTO and CPT jobs, adapter versioning and rollback, and on-demand, scheduled or always-on serving.",
  },
  {
    value: SPEC_A2A,
    label: "A2A",
    intro:
      "Agent-to-Agent protocol — agent discovery, messages and tasks so independent agents can find each other and delegate work.",
  },
];

function getSpecMeta(spec) {
  return SPECS.find((s) => s.value === spec);
}

// Agents (aiopenapi.json) - exact paths and prefixes
const TF_AGENTS_PATHS = new Set([
  "/agent",
  "/planner",
  "/predictions",
  "/v1/chat/completions",
  "/chat/completions",
  "/spawn-agent",
  "/searcher",
  "/tokenizer",
  "/embedder",
  "/magicwand",
  "/models_list",
  "/dispatch",
]);
const TF_AGENTS_PREFIXES = ["/dispatch/", "/test/"];

// Voice (voiceapi.json)
const TF_VOICE_PATHS = new Set(["/token", "/webhook", "/health"]);
const TF_VOICE_PREFIXES = ["/sip/"];

// Media (mediaapi.json) - exact paths; prefixes cover the polling routes
const TF_MEDIA_PATHS = new Set([
  "/media/audio_generation",
  "/media/voices",
  "/media/voices/clone",
  "/media/image_generation",
  "/media/image",
  "/media/video_generation",
]);
const TF_MEDIA_PREFIXES = ["/media/"];

// Live (liveapi.json) - exact paths; prefix covers the session sub-routes
const TF_LIVE_PATHS = new Set(["/live"]);
const TF_LIVE_PREFIXES = ["/live/"];

// A2A (a2aapi.json) - exact paths only
const TF_A2A_PATHS = new Set(["/.well-known/agent.json", "/"]);

// Fine-Tuning (finetuningapi.json)
const TF_FINETUNING_PATHS = new Set([
  "/models",
  "/jobs",
  "/dataset",
  "/adapters",
]);
const TF_FINETUNING_PREFIXES = [
  "/status/",
  "/start/",
  "/cancel/",
  "/dataset/",
  "/adapters/",
];

// RapiDoc tag element ids are "tag--{name}" with invalid chars replaced by "-".
// Maps non-default spec tags to their spec file. App tags resolve via the default.
// "Agents" exists in BOTH openapi.json and (historically) aiopenapi.json — it maps
// to the App spec (agent configuration) because aiopenapi's runtime tag is now the
// unambiguous "Agent Runtime". Legacy names (Inference, AI Predictions, Orchestrator,
// Async-Tasks, Agent) stay mapped so older #tag-... links keep resolving.
const TAG_TO_SPEC = {
  Agents: SPEC_APP,
  "Agent-Runtime": SPEC_AGENTS,
  Predictions: SPEC_AGENTS,
  "Inference-Utilities": SPEC_AGENTS,
  Inference: SPEC_AGENTS,
  Agent: SPEC_AGENTS,
  "AI-Predictions": SPEC_AGENTS,
  "Async-Tasks": SPEC_AGENTS,
  Orchestrator: SPEC_AGENTS,
  Authentication: SPEC_VOICE,
  Health: SPEC_VOICE,
  Telephony: SPEC_VOICE,
  "Agent-Discovery": SPEC_A2A,
  Messages: SPEC_A2A,
  Tasks: SPEC_A2A,
  "Live-Sessions": SPEC_LIVE,
  Models: SPEC_FINETUNING,
  Jobs: SPEC_FINETUNING,
  Dataset: SPEC_FINETUNING,
  Training: SPEC_FINETUNING,
  Adapters: SPEC_FINETUNING,
  "Fine-Tuning": SPEC_FINETUNING, // legacy tag kept alive for older links
  "Speech-Generation": SPEC_MEDIA,
  "Voice-Management": SPEC_MEDIA,
  "Task-Polling": SPEC_MEDIA,
  "Image-Generation": SPEC_MEDIA,
  "Video-Generation": SPEC_MEDIA,
};

/**
 * Parses the URL hash into a routing target.
 * RapiDoc element ids: "{method}-{path}" for operations (invalid chars
 * [\s#:?&={}] replaced by "-"), "tag--{name}" for tags, "overview" etc. for sections.
 *
 * @param {string} hash - window.location.hash (including leading #)
 * @returns {{elementId: string, kind: string, endpoint?: string, tagName?: string} | null}
 */
function parseHashTarget(hash) {
  if (!hash || hash.length <= 1) {
    return null;
  }

  let elementId = hash.substring(1);
  try {
    elementId = decodeURIComponent(elementId);
  } catch (e) {
    // keep raw elementId if decoding fails
  }

  // Canonical RapiDoc id form: path params {id} -> -id-
  elementId = elementId.replace(/\{([^}]+)\}/g, "-$1-");
  // Tolerate single-dash tag links (e.g. #tag-Agents -> #tag--Agents)
  if (elementId.startsWith("tag-") && !elementId.startsWith("tag--")) {
    elementId = `tag--${elementId.substring(4)}`;
  }

  if (!elementId) {
    return null;
  }

  if (elementId.startsWith("tag--")) {
    // Legacy alias: the old "Inference" tag was split into "Predictions" and
    // "Inference Utilities" — send its links to the nearest semantic home.
    if (elementId === "tag--Inference") {
      return {
        elementId: "tag--Predictions",
        kind: "tag",
        tagName: "Predictions",
      };
    }
    return { elementId, kind: "tag", tagName: elementId.substring(5) };
  }

  if (
    elementId === "overview" ||
    elementId === "auth" ||
    elementId === "servers"
  ) {
    return { elementId, kind: "section" };
  }

  if (elementId.includes("-/")) {
    const endpoint = elementId
      .replace(/^[^-]+-/, "") // Remove method prefix (e.g., "get-")
      .replace(/-([^-/]+)-/g, "{$1}"); // Convert -id- back to {id}
    return { elementId, kind: "operation", endpoint };
  }

  return { elementId, kind: "unknown" };
}

/**
 * Determines the correct spec file for a given endpoint path.
 * Returns null when nothing matches explicitly (caller falls back to scanning).
 */
function getSpecForEndpoint(endpoint) {
  const normalizedEndpoint = endpoint.replace(/\{[^}]+\}/g, "");

  if (
    TF_AGENTS_PATHS.has(endpoint) ||
    TF_AGENTS_PATHS.has(normalizedEndpoint)
  ) {
    return SPEC_AGENTS;
  }
  for (const prefix of TF_AGENTS_PREFIXES) {
    if (endpoint.startsWith(prefix) || normalizedEndpoint.startsWith(prefix)) {
      return SPEC_AGENTS;
    }
  }

  if (TF_A2A_PATHS.has(endpoint)) {
    return SPEC_A2A;
  }

  if (TF_VOICE_PATHS.has(endpoint) || TF_VOICE_PATHS.has(normalizedEndpoint)) {
    return SPEC_VOICE;
  }
  for (const prefix of TF_VOICE_PREFIXES) {
    if (endpoint.startsWith(prefix) || normalizedEndpoint.startsWith(prefix)) {
      return SPEC_VOICE;
    }
  }

  if (
    TF_FINETUNING_PATHS.has(endpoint) ||
    TF_FINETUNING_PATHS.has(normalizedEndpoint)
  ) {
    return SPEC_FINETUNING;
  }
  for (const prefix of TF_FINETUNING_PREFIXES) {
    if (endpoint.startsWith(prefix) || normalizedEndpoint.startsWith(prefix)) {
      return SPEC_FINETUNING;
    }
  }

  if (TF_LIVE_PATHS.has(endpoint) || TF_LIVE_PATHS.has(normalizedEndpoint)) {
    return SPEC_LIVE;
  }
  for (const prefix of TF_LIVE_PREFIXES) {
    if (endpoint.startsWith(prefix) || normalizedEndpoint.startsWith(prefix)) {
      return SPEC_LIVE;
    }
  }

  if (TF_MEDIA_PATHS.has(endpoint) || TF_MEDIA_PATHS.has(normalizedEndpoint)) {
    return SPEC_MEDIA;
  }
  for (const prefix of TF_MEDIA_PREFIXES) {
    if (endpoint.startsWith(prefix) || normalizedEndpoint.startsWith(prefix)) {
      return SPEC_MEDIA;
    }
  }

  return SPEC_APP;
}

/**
 * Resolves which spec should contain the given target.
 * Returns null for unknown targets (fallback scan will locate the element).
 */
function getSpecForTarget(target, currentSpec) {
  if (target.kind === "tag") {
    return TAG_TO_SPEC[target.tagName] || null;
  }
  if (target.kind === "section") {
    // overview/auth/servers exist in every spec - stay where we are
    return currentSpec || SPEC_APP;
  }
  if (target.kind === "operation") {
    return getSpecForEndpoint(target.endpoint);
  }
  return null;
}

function App() {
  const [specUrl, setSpecUrl] = useState(() => {
    const target = parseHashTarget(window.location.hash);
    return (target && getSpecForTarget(target, null)) || SPEC_APP;
  });
  const [specReady, setSpecReady] = useState(false);
  const rapiDocRef = useRef(null);
  const targetRef = useRef(parseHashTarget(window.location.hash));
  const triedSpecsRef = useRef([specUrl]);
  const suppressNextHashChangeRef = useRef(false);
  const lastHandledElementRef = useRef(null);

  // Scrolls to the pending target if the loaded spec contains it, otherwise
  // scans the remaining spec files until the element is found.
  const resolveTarget = useCallback(() => {
    const rapiDoc = rapiDocRef.current;
    if (!rapiDoc || !targetRef.current) {
      return;
    }

    // Small delay to ensure rendering is complete
    setTimeout(() => {
      const target = targetRef.current;
      if (!target) {
        return;
      }

      const element =
        rapiDoc.shadowRoot &&
        rapiDoc.shadowRoot.getElementById(target.elementId);
      if (element) {
        rapiDoc.scrollToPath(target.elementId);
        lastHandledElementRef.current = target.elementId;
        targetRef.current = null;
        // Restore the original hash in case RapiDoc rewrote it
        setTimeout(() => {
          if (window.location.hash.substring(1) !== target.elementId) {
            suppressNextHashChangeRef.current = true;
            window.location.hash = target.elementId;
          }
        }, 100);
        return;
      }

      // Element not in this spec - try the remaining specs
      const tried = triedSpecsRef.current;
      const nextSpec = ALL_SPECS.find((spec) => !tried.includes(spec));
      if (nextSpec) {
        tried.push(nextSpec);
        setSpecUrl(nextSpec);
      } else {
        // Nowhere to be found - land back on the best-guess spec and drop the
        // dangling hash so the URL never claims a target we could not show
        targetRef.current = null;
        suppressNextHashChangeRef.current = true;
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
        if (tried[0] && tried[tried.length - 1] !== tried[0]) {
          setSpecUrl(tried[0]);
        }
      }
    }, 500);
  }, []);

  // Set the spec url on the rapi-doc element (single load, no mount race)
  useEffect(() => {
    const rapiDoc = document.querySelector("rapi-doc");
    if (rapiDoc) {
      rapiDocRef.current = rapiDoc;
      if (rapiDoc.getAttribute("spec-url") !== specUrl) {
        rapiDoc.setAttribute("spec-url", specUrl);
      }
    }
  }, [specUrl]);

  // Navigate once each spec finishes loading
  useEffect(() => {
    const rapiDoc = document.querySelector("rapi-doc");
    if (!rapiDoc) {
      return undefined;
    }
    const handleSpecLoaded = () => {
      setSpecReady(true);
      resolveTarget();
    };
    rapiDoc.addEventListener("spec-loaded", handleSpecLoaded);
    return () => rapiDoc.removeEventListener("spec-loaded", handleSpecLoaded);
  }, [resolveTarget]);

  // Keep the tab title in sync with the loaded spec
  useEffect(() => {
    const label = getSpecMeta(specUrl)?.label;
    document.title = label
      ? `${label} — ToothFairyAI API docs`
      : "ToothFairyAI API docs";
  }, [specUrl]);

  // Failsafe: never trap the user behind the loading veil
  useEffect(() => {
    if (specReady) {
      return undefined;
    }
    const failsafe = setTimeout(() => setSpecReady(true), 15000);
    return () => clearTimeout(failsafe);
  }, [specReady]);

  // Handle hash changes (e.g. user pastes a different endpoint link)
  useEffect(() => {
    const handleHashChange = () => {
      if (suppressNextHashChangeRef.current) {
        suppressNextHashChangeRef.current = false;
        return;
      }

      const target = parseHashTarget(window.location.hash);
      if (!target || target.elementId === lastHandledElementRef.current) {
        return;
      }

      const rapiDoc = rapiDocRef.current;
      const element =
        rapiDoc &&
        rapiDoc.shadowRoot &&
        rapiDoc.shadowRoot.getElementById(target.elementId);

      if (element) {
        // Element exists in the currently loaded spec - just scroll
        rapiDoc.scrollToPath(target.elementId);
        lastHandledElementRef.current = target.elementId;
        return;
      }

      const targetSpec = getSpecForTarget(target, specUrl) || SPEC_APP;
      targetRef.current = target;
      triedSpecsRef.current = [targetSpec];
      if (targetSpec !== specUrl) {
        setSpecUrl(targetSpec);
      } else {
        // Mapped to the current spec but element missing - scan the rest
        resolveTarget();
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [specUrl, resolveTarget]);

  const handleSpecChange = (event) => {
    const newSpec = event.target.value;
    targetRef.current = null;
    triedSpecsRef.current = [newSpec];
    setSpecReady(false);
    setSpecUrl(newSpec);
    // The dropdown changes the page — reflect that in the URL so a copied
    // link reproduces exactly what the sender is looking at
    suppressNextHashChangeRef.current = true;
    window.location.hash = "overview";
  };

  const handleGuideChange = (event) => {
    const value = event.target.value;
    if (value !== "none") {
      const guide = GUIDES.find((g) => g.value === value);
      if (guide) {
        window.open(guide.url, "_blank", "noopener,noreferrer");
      }
      // Reset dropdown to placeholder
      event.target.value = "none";
    }
  };

  const specMeta = getSpecMeta(specUrl) || SPECS[0];

  return (
    <div className="apidocs-root">
      <header className="apidocs-header">
        <div className="apidocs-header-left">
          <span className="apidocs-section-name">{specMeta.label}</span>
          <span className="apidocs-section-intro" title={specMeta.intro}>
            {specMeta.intro}
          </span>
        </div>

        <div className="apidocs-controls">
          <div className="apidocs-control-group">
            <label htmlFor="api-selector">API</label>
            <select
              id="api-selector"
              onChange={handleSpecChange}
              value={specUrl}
            >
              {SPECS.map((spec) => (
                <option key={spec.value} value={spec.value}>
                  {spec.label}
                </option>
              ))}
            </select>
          </div>

          <div className="apidocs-control-group">
            <label htmlFor="guide-selector">Guides</label>
            <select
              id="guide-selector"
              onChange={handleGuideChange}
              defaultValue="none"
            >
              <option value="none">Select Guide...</option>
              {GUIDES.map((guide) => (
                <option key={guide.value} value={guide.value}>
                  {guide.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <div className="apidocs-doc-wrap">
        <rapi-doc
          render-style="read"
          theme="dark"
          bg-color="#202021"
          text-color="#e0e0e0"
          primary-color="#1db8c6"
          nav-bg-color="#262628"
          nav-text-color="#c9c9ce"
          nav-hover-bg-color="#303033"
          nav-hover-text-color="#ffffff"
          nav-accent-color="#1db8c6"
          nav-item-spacing="relaxed"
          regular-font="'Gilroy', Arial, sans-serif"
          mono-font="'Courier New', monospace"
          nav-logo="/logo.png"
          header-color="#262628"
          code-theme="dark"
          show-header={false}
          css-file="/rapidoc-overrides.css"
        >
          <img
            alt="ToothFairyAI"
            slot="nav-logo"
            src="/logo.png"
            style={{ width: "120px" }}
          />
        </rapi-doc>

        <div
          className={`apidocs-veil${specReady ? " apidocs-veil--hidden" : ""}`}
          aria-hidden={specReady}
        >
          <img src="/logo.png" alt="" />
          <div className="apidocs-veil-spinner" />
          <div className="apidocs-veil-text">Loading API reference</div>
        </div>
      </div>
    </div>
  );
}

export default App;
